<template>
<form class="pl-ef" @submit.prevent="save" @keydown.esc.prevent.stop="$emit('cancel')" @click.stop>
    <div class="pl-ef-head">{{ entry ? 'Edit' : 'Add' }} · {{ fmtDay(dateVal || date) }}</div>

    <!-- kind: the magnet's colour -->
    <div class="pl-ef-kinds" role="radiogroup" aria-label="Kind">
        <button v-for="k in kinds" :key="k.id" type="button" class="pl-ef-kind"
                :class="{ on: kind === k.id }" :style="{ '--kc': k.colour }"
                :aria-pressed="kind === k.id" @click="setKind(k.id)">{{ k.label }}</button>
    </div>

    <!-- job: search the jobs in flight; optional for events -->
    <label class="pl-ef-lbl">Job <small v-if="kind === 'event'">(optional)</small></label>
    <div v-if="selectedJob" class="pl-ef-job">
        <span class="pl-ef-job-nm" :title="jobTitle(selectedJob)">{{ selectedJob.quote_name }}</span>
        <small v-if="selectedJob.company" class="pl-ef-job-co">{{ selectedJob.company }}</small>
        <button type="button" class="pl-ef-x" aria-label="Remove job" title="No job" @click="clearJob">✕</button>
    </div>
    <div v-else class="pl-ef-search">
        <input ref="job" type="text" class="pl-ef-in" v-model="jobQuery" autocomplete="off"
               :placeholder="jobs.length ? 'Client, company or Q number' : 'No jobs in flight'"
               @focus="showJobs = true" @input="showJobs = true; jobIndex = 0"
               @keydown.down.prevent="moveJob(1)" @keydown.up.prevent="moveJob(-1)"
               @keydown.enter="pickOnEnter($event)">
        <div v-if="showJobs && jobQuery.trim() && matches.length" class="pl-ef-dd">
            <button v-for="(j, i) in matches" :key="j.quote_id" type="button" class="pl-ef-opt"
                    :class="{ on: i === jobIndex }" @mousedown.prevent="pickJob(j)">
                <b>{{ j.quote_name }}</b>
                <small>Q{{ j.quote_id }}<template v-if="j.company"> · {{ j.company }}</template><template v-if="j.deadline"> · ⚑ {{ fmtDay(j.deadline) }}</template></small>
            </button>
        </div>
        <div v-else-if="showJobs && jobQuery.trim() && !matches.length" class="pl-ef-dd pl-ef-none">
            No job matches — leave it and give the entry a title
        </div>
    </div>

    <!-- title: free text, the magnet's name; defaults to the job's -->
    <label class="pl-ef-lbl" for="pl-ef-title">Title <small v-if="!selectedJob">(required)</small></label>
    <input id="pl-ef-title" ref="title" type="text" class="pl-ef-in" v-model="title" maxlength="255"
           :placeholder="titlePlaceholder">

    <div class="pl-ef-row">
        <div class="pl-ef-col">
            <label class="pl-ef-lbl" for="pl-ef-date">Date</label>
            <input id="pl-ef-date" type="date" class="pl-ef-in" v-model="dateVal" required>
        </div>
        <div class="pl-ef-col pl-ef-col-money">
            <label class="pl-ef-lbl" for="pl-ef-cost">£</label>
            <input id="pl-ef-cost" type="number" class="pl-ef-in" v-model="pounds" min="0" step="0.01"
                   inputmode="decimal" placeholder="0.00">
        </div>
    </div>

    <label class="pl-ef-chk">
        <input type="checkbox" v-model="tbc"> <span>tbc — date not fixed yet</span>
    </label>

    <label class="pl-ef-lbl" for="pl-ef-note">Note</label>
    <input id="pl-ef-note" type="text" class="pl-ef-in" v-model="note" maxlength="2000"
           placeholder="e.g. 2m×1.4m oak tray · ring first">

    <div v-if="problem" class="pl-ef-problem">{{ problem }}</div>

    <div class="pl-ef-acts">
        <button type="button" class="pl-ef-btn" @click="$emit('cancel')">Cancel</button>
        <button type="submit" class="pl-ef-btn pl-ef-btn-primary" :disabled="!canSave">{{ entry ? 'Save' : 'Add' }}</button>
    </div>
</form>
</template>

<script>
import { fmtDay } from './shared';

/**
 * The Dates board's add / edit form — one magnet's worth of fields, rendered
 * inline in the day cell that was tapped (planning/planner-boards-p1-p3-p4-plan.md §4):
 * kind · job (search over the jobs in flight; optional for an event) · title
 * (defaults to the job's name) · date · £ · tbc · note.
 *
 * Props
 *   date   Y-m-d the cell that opened it — prefilled
 *   entry  an existing board entry to edit, or null for a new one
 *   jobs   board.jobs — { quote_id, quote_name, company, deadline, flags }
 *
 * Emits
 *   save(payload)  { kind, quote_id, title, scheduled_date, cost_pence, tbc, note }
 *                  — the PlannerEventController body; the parent posts it.
 *   cancel
 *
 * Enter saves (the job dropdown eats it while it has a highlighted match),
 * Esc cancels. Sized for a 150px column and an iPad thumb.
 */
export default {
    name: 'EventForm',

    props: {
        date: { type: String, required: true },
        entry: { type: Object, default: null },
        jobs: { type: Array, default: () => [] },
    },

    data() {
        const e = this.entry;
        // A stored NULL title reads back as the job's name — keep the field
        // empty in that case so it keeps tracking the job.
        const ownTitle = e && e.title && e.title !== e.quote_name ? e.title : '';
        return {
            kinds: [
                { id: 'delivery', label: 'Delivery', colour: '#e8642c' },
                { id: 'collection', label: 'Collection', colour: '#3aa6a6' },
                { id: 'install', label: 'Install', colour: '#1d3f8a' },
                { id: 'event', label: 'Event', colour: '#898781' },
            ],
            kind: e ? e.kind : 'delivery',
            quoteId: e && e.quote_id ? e.quote_id : null,
            jobQuery: '',
            showJobs: false,
            jobIndex: 0,
            title: ownTitle,
            dateVal: e ? e.date : this.date,
            pounds: e && e.cost_pence !== null && e.cost_pence !== undefined ? (e.cost_pence / 100).toFixed(2) : '',
            tbc: e ? !!e.tbc : false,
            note: e && e.note ? e.note : '',
            problem: null,
        };
    },

    computed: {
        selectedJob() {
            if (!this.quoteId) return null;
            const j = this.jobs.find(x => x.quote_id === this.quoteId);
            if (j) return j;
            // editing an entry whose job has since left the lane (finished)
            if (this.entry && this.entry.quote_id === this.quoteId) {
                return { quote_id: this.quoteId, quote_name: this.entry.quote_name || ('Q' + this.quoteId), company: this.entry.company };
            }
            return { quote_id: this.quoteId, quote_name: 'Q' + this.quoteId, company: null };
        },
        matches() {
            const q = this.jobQuery.trim().toLowerCase();
            if (!q) return [];
            const qn = q.replace(/^q/, '');
            return this.jobs.filter(j => {
                if (String(j.quote_id) === qn) return true;
                if ((j.quote_name || '').toLowerCase().indexOf(q) !== -1) return true;
                return (j.company || '').toLowerCase().indexOf(q) !== -1;
            }).slice(0, 8);
        },
        titlePlaceholder() {
            if (this.selectedJob) return this.selectedJob.quote_name + ' (from the job)';
            if (this.kind === 'event') return 'e.g. Bank holiday · Van in for MOT';
            return 'Who / what';
        },
        canSave() {
            if (!this.kind || !this.dateVal) return false;
            if (!this.quoteId && !this.title.trim()) return false;
            if (this.pounds !== '' && (isNaN(parseFloat(this.pounds)) || parseFloat(this.pounds) < 0)) return false;
            return true;
        },
    },

    mounted() {
        this.$nextTick(() => {
            const el = (!this.entry && this.kind !== 'event' && !this.quoteId) ? this.$refs.job : this.$refs.title;
            if (el && el.focus) el.focus();
        });
        document.addEventListener('mousedown', this.onDocDown);
    },
    beforeDestroy() {
        document.removeEventListener('mousedown', this.onDocDown);
    },

    methods: {
        fmtDay,
        setKind(k) {
            this.kind = k;
            this.problem = null;
        },
        jobTitle(j) {
            return 'Q' + j.quote_id + ' · ' + j.quote_name + (j.company ? ' · ' + j.company : '');
        },
        pickJob(j) {
            this.quoteId = j.quote_id;
            this.jobQuery = '';
            this.showJobs = false;
            this.problem = null;
            this.$nextTick(() => { if (this.$refs.title) this.$refs.title.focus(); });
        },
        clearJob() {
            this.quoteId = null;
            this.$nextTick(() => { if (this.$refs.job) this.$refs.job.focus(); });
        },
        moveJob(delta) {
            if (!this.matches.length) return;
            this.showJobs = true;
            this.jobIndex = (this.jobIndex + delta + this.matches.length) % this.matches.length;
        },
        pickOnEnter(ev) {
            if (this.showJobs && this.jobQuery.trim() && this.matches.length) {
                ev.preventDefault();
                ev.stopPropagation();
                this.pickJob(this.matches[Math.min(this.jobIndex, this.matches.length - 1)]);
            }
            // otherwise the form's submit handles Enter
        },
        onDocDown(ev) {
            if (!this.$el.contains(ev.target)) this.showJobs = false;
        },
        save() {
            if (!this.canSave) {
                this.problem = !this.quoteId && !this.title.trim()
                    ? 'Pick a job or give it a title'
                    : 'Check the date and the £';
                return;
            }
            const pounds = this.pounds === '' ? null : parseFloat(this.pounds);
            this.$emit('save', {
                kind: this.kind,
                quote_id: this.quoteId || null,
                title: this.title.trim() || null,
                scheduled_date: this.dateVal,
                cost_pence: pounds === null ? null : Math.round(pounds * 100),
                tbc: !!this.tbc,
                note: this.note.trim() || null,
            });
        },
    },
};
</script>

<style scoped>
.pl-ef{display:grid; gap:6px; background:#fcfcfb; border:1px solid #0b0b0b; border-radius:10px; padding:10px;
    box-shadow:0 8px 24px rgba(11,11,11,.14); font-size:12.5px; color:#0b0b0b; text-align:left; cursor:default}
.pl-ef *{box-sizing:border-box}
.pl-ef-head{font-weight:800; font-size:12px; color:#52514e; letter-spacing:.02em; text-transform:uppercase}
.pl-ef-kinds{display:grid; grid-template-columns:1fr 1fr; gap:4px}
.pl-ef-kind{border:1px solid #e1e0d9; border-left:4px solid var(--kc); background:#fcfcfb; color:#0b0b0b;
    border-radius:7px; padding:7px 6px; font:inherit; font-weight:700; font-size:12px; min-height:34px; cursor:pointer; text-align:left}
.pl-ef-kind.on{background:var(--kc); border-color:var(--kc); color:#fff}
.pl-ef-lbl{font-weight:700; font-size:11px; color:#52514e; margin-top:2px}
.pl-ef-lbl small{font-weight:600; color:#898781}
.pl-ef-in{width:100%; border:1px solid #e1e0d9; border-radius:7px; padding:7px 9px; background:#fff;
    font:inherit; font-size:13px; color:#0b0b0b; min-height:36px}
.pl-ef-in:focus{outline:2px solid #0b0b0b; outline-offset:-1px}
/* the match list flows inline (the board grid clips overflow, so a
   popover on the bottom row would be cut off) — the form grows to fit */
.pl-ef-dd{background:#fcfcfb; border:1px solid #e1e0d9;
    border-radius:8px; box-shadow:0 2px 8px rgba(11,11,11,.08); max-height:180px; overflow:auto; margin-top:3px}
.pl-ef-none{padding:8px 10px; color:#898781; font-size:11.5px}
.pl-ef-opt{display:grid; gap:1px; width:100%; text-align:left; border:0; border-bottom:1px solid #f1f0eb;
    background:transparent; padding:7px 10px; font:inherit; font-size:12px; color:#0b0b0b; cursor:pointer; min-height:36px}
.pl-ef-opt:last-child{border-bottom:0}
.pl-ef-opt small{color:#898781; font-size:11px}
.pl-ef-opt.on, .pl-ef-opt:hover{background:#f4f3f0}
.pl-ef-job{display:flex; align-items:center; gap:6px; border:1px solid #0b0b0b; border-radius:7px; padding:6px 8px; min-height:36px; background:#fff}
.pl-ef-job-nm{font-weight:700; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; min-width:0}
.pl-ef-job-co{color:#898781; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; min-width:0}
.pl-ef-x{margin-left:auto; border:0; background:transparent; color:#52514e; font-weight:800; cursor:pointer; padding:2px 6px; font:inherit; flex:none}
.pl-ef-x:hover{color:#d03b3b}
.pl-ef-row{display:flex; gap:6px; flex-wrap:wrap}
.pl-ef-col{flex:1 1 110px; min-width:0; display:grid; gap:4px}
.pl-ef-col-money{flex:0 1 84px}
.pl-ef-chk{display:flex; align-items:center; gap:7px; font-weight:600; color:#0b0b0b; min-height:32px; cursor:pointer}
.pl-ef-chk input{width:18px; height:18px; margin:0}
.pl-ef-problem{color:#b32e2e; font-weight:700; font-size:11.5px}
.pl-ef-acts{display:flex; gap:6px; justify-content:flex-end; margin-top:4px}
.pl-ef-btn{border:1px solid #e1e0d9; background:#fcfcfb; color:#0b0b0b; border-radius:8px; padding:8px 12px;
    font:inherit; font-weight:700; font-size:12.5px; min-height:36px; cursor:pointer}
.pl-ef-btn-primary{background:#0b0b0b; border-color:#0b0b0b; color:#fff}
.pl-ef-btn:disabled{opacity:.5; cursor:default}
</style>
